import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createNoteSchema } from '../utils/validation';
import { createNote } from '../services/notes';
import { getPasswordStrength, checkPassword } from '../utils/passwords';
import { verifyReCaptcha } from '../services/recaptcha';
import useReCaptcha from './useReCaptcha';
import { useReCaptchaContext } from '../contexts/ReCaptchaContext';

interface UseNoteCreationReturn {
  content: string;
  setContent: (content: string) => void;
  password: string;
  setPassword: (password: string) => void;
  expiresInDays: number;
  setExpiresInDays: (days: number) => void;
  viewLimit: number | null;
  setViewLimit: (limit: number | null) => void;
  isProtected: boolean;
  setIsProtected: (protected: boolean) => void;
  isSubmitting: boolean;
  passwordStrength: { score: number; feedback: string } | null;
  isCheckingPassword: boolean;
  isPasswordExposed: boolean;
  isPasswordValidated: boolean;
  toast: { message: string; type: 'error' | 'success' | 'info' } | null;
  setToast: (toast: { message: string; type: 'error' | 'success' | 'info' } | null) => void;
  handleSubmit: (e: React.FormEvent) => Promise<{ short_id: string } | undefined>;
  handleProtectionToggle: () => void;
  isSubmitDisabled: boolean;
}

export default function useNoteCreation(): UseNoteCreationReturn {
  const { t } = useTranslation();
  const { getReCaptchaToken } = useReCaptcha();
  const { isEnabled: recaptchaEnabled } = useReCaptchaContext();
  const [content, setContent] = useState('');
  const [password, setPassword] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [viewLimit, setViewLimit] = useState<number | null>(null);
  const [isProtected, setIsProtected] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' | 'info' } | null>(null);
  const [passwordStrength, setPasswordStrength] = useState<{ score: number; feedback: string } | null>(null);
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);
  const [isPasswordExposed, setIsPasswordExposed] = useState(false);
  const [isPasswordValidated, setIsPasswordValidated] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const checkPasswordSecurity = async () => {
      if (isProtected && password.length >= 6) {
        setIsCheckingPassword(true);
        setIsPasswordValidated(false);
        const { exposed } = await checkPassword(password);
        setIsPasswordExposed(exposed);
        const strength = await getPasswordStrength(password);
        setPasswordStrength(strength);
        setIsCheckingPassword(false);
        setIsPasswordValidated(true);
      } else {
        setPasswordStrength(null);
        setIsPasswordExposed(false);
        setIsPasswordValidated(!isProtected);
      }
    };

    if (isProtected && password.length > 0) {
      setIsPasswordValidated(false);
      timeout = setTimeout(checkPasswordSecurity, 500);
    } else {
      setPasswordStrength(null);
      setIsPasswordExposed(false);
      setIsPasswordValidated(!isProtected);
    }

    return () => clearTimeout(timeout);
  }, [password, isProtected]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setToast(null);

    if (!content.trim()) {
      setToast({
        message: t('createNote.form.content.validation.required'),
        type: 'error'
      });
      return;
    }

    if (isProtected && !isPasswordValidated) {
      setToast({
        message: t('createNote.form.validation.passwordValidation'),
        type: 'error'
      });
      return;
    }

    if (isProtected && isPasswordExposed) {
      setToast({
        message: t('createNote.form.password.validation.exposed'),
        type: 'error'
      });
      return;
    }

    if (viewLimit !== null && viewLimit < 1) {
      setToast({
        message: t('createNote.form.viewLimit.validation.positive'),
        type: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let isVerified = true;
      if (recaptchaEnabled) {
        const token = await getReCaptchaToken('create_note');
        isVerified = await verifyReCaptcha(token, 'create_note');
      }

      if (!isVerified) {
        setToast({
          message: t('createNote.form.validation.recaptcha'),
          type: 'error'
        });
        setIsSubmitting(false);
        return;
      }

      const data = { 
        content: content.trim(), 
        password: isProtected ? password : undefined, 
        expiresInDays,
        viewLimit,
        generateAutopass: !isProtected
      };
      
      await createNoteSchema.parseAsync(data);
      const result = await createNote(data);
      return result;
    } catch (err) {
      if (err instanceof Error) {
        setToast({ message: err.message, type: 'error' });
      } else {
        setToast({ message: t('createNote.form.validation.error'), type: 'error' });
      }
      setIsSubmitting(false);
    }
  };

  const handleProtectionToggle = () => {
    setIsProtected(!isProtected);
    setPassword('');
    setToast(null);
    setPasswordStrength(null);
    setIsPasswordValidated(!isProtected);
  };

  const isPasswordValid = !isProtected || (password.length >= 6 && !isPasswordExposed && isPasswordValidated);
  const isSubmitDisabled = !content.trim() || !isPasswordValid || isSubmitting;

  return {
    content,
    setContent,
    password,
    setPassword,
    expiresInDays,
    setExpiresInDays,
    viewLimit,
    setViewLimit,
    isProtected,
    setIsProtected,
    isSubmitting,
    passwordStrength,
    isCheckingPassword,
    isPasswordExposed,
    isPasswordValidated,
    toast,
    setToast,
    handleSubmit,
    handleProtectionToggle,
    isSubmitDisabled,
  };
}